<template>
<div class="pl-root sd-root">

    <!-- notices -->
    <div class="sd-notice" v-for="n in extras.notices" :key="'n' + n.id" :class="'sd-notice-' + n.type">
        <span>{{ n.message }}</span>
        <button type="button" @click="dismissNotice(n)">✕</button>
    </div>

    <div v-if="error" class="pl-error">{{ error }} <button type="button" @click="error = null">✕</button></div>

    <!-- header: who + stats strip -->
    <div class="sd-head">
        <div class="sd-title">
            <span class="pl-av">{{ initials(extras.user_name || '') }}</span>
            <div>
                <h1>My day</h1>
                <div class="sd-date">{{ todayLabel }}</div>
            </div>
        </div>
        <div class="sd-stats" v-if="extras.stats">
            <div class="sd-stat"><b>{{ extras.stats.completed_today }}</b><span>done today</span></div>
            <div class="sd-stat"><b>{{ extras.stats.percent }}%</b><span>my day so far</span></div>
            <div class="sd-stat" v-if="weekPercent !== null"><b>{{ weekPercent }}%</b><span>this week</span></div>
            <a class="sd-stat sd-stat-link" href="/dashboard/my-efficiency" data-ajax="false"><b>→</b><span>my efficiency</span></a>
            <button v-if="extras.feed_url" type="button" class="sd-stat sd-stat-link" @click="calOpen = !calOpen">
                <b>📅</b><span>{{ calOpen ? 'close' : 'calendar' }}</span>
            </button>
        </div>
    </div>

    <!-- connect your calendar -->
    <div class="sd-calconnect" v-if="calOpen && extras.feed_url">
        <h3>📅 Connect your calendar</h3>
        <p>Your planned work on your own phone — it updates by itself once you're subscribed.</p>
        <div class="sd-cal-row">
            <input readonly :value="extras.feed_url" @focus="$event.target.select()">
            <button type="button" @click="copyFeed">{{ calCopied ? '✓ Copied' : 'Copy link' }}</button>
        </div>
        <ol>
            <li><b>Google Calendar:</b> Settings → Add calendar → From URL → paste the link.</li>
            <li><b>iPhone:</b> Settings → Apps → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar → paste.</li>
            <li><b>Outlook:</b> Add calendar → Subscribe from web → paste.</li>
        </ol>
    </div>

    <!-- currently working -->
    <div class="sd-current" v-if="extras.current_work">
        <div class="sd-current-badge"><svg class="pl-i"><use href="#pl-zap"/></svg>Working on now</div>
        <div class="sd-current-nm">{{ extras.current_work.process_name }}</div>
        <div class="sd-current-meta">
            <template v-if="extras.current_work.quote_id">Q{{ extras.current_work.quote_id }} · {{ extras.current_work.quote_name }} · </template>
            est {{ fmtSecs(extras.current_work.est_seconds || 0) }}
        </div>
        <button type="button" class="sd-current-done" :disabled="busy" @click="completeCurrent">✓ Finished it</button>
    </div>

    <div v-if="loading && !board.dates" class="pl-loading">Loading your day…</div>

    <!-- carryover -->
    <div class="sd-section" v-if="myCarryover.length">
        <h2>⚠ Unfinished from earlier days</h2>
        <div class="sd-carry" v-for="c in myCarryover" :key="'c' + c.slot_id">
            <div class="sd-carry-tx">
                <b>{{ c.pretty_name }}</b>
                <span v-if="c.quote_id">Q{{ c.quote_id }} · {{ c.quote_name }} · </span>planned for {{ c.date }}
            </div>
            <button type="button" class="sd-btn" :disabled="busy" @click="moveSlotToToday(c.slot_id)">Move to today</button>
            <button type="button" class="sd-btn sd-btn-good" :disabled="busy" @click="tickChecklist(c.checklist_id, true)">✓ Done</button>
        </div>
    </div>

    <!-- today -->
    <div class="sd-section">
        <h2>Today <span class="sd-count">{{ doneToday.length }} of {{ todaySlots.length + doneToday.length }} done</span></h2>

        <draggable v-if="todaySlots.length" class="sd-list"
                   :list="todaySlots"
                   handle=".sd-grip"
                   @change="onReorder">
            <div v-for="slot in todaySlots" :key="slot.id" class="pl-chip sd-chip"
                 :style="{ '--plwc': wc.colour(slot.workcentre_id, slot.type) }"
                 :title="chipTitle(slot)"
                 @click="drawerChip = slot">
                <span class="sd-grip" title="drag to reorder" @click.stop><svg class="pl-i"><use href="#pl-grip"/></svg></span>
                <svg class="pl-i sd-chip-i"><use :href="'#' + wc.icon(slot.workcentre_id, slot.type)"/></svg>
                <div class="pl-chip-cx">
                    <div class="pl-chip-r1">
                        <span class="pl-chip-nm">{{ slot.pretty_name }}</span>
                        <span class="pl-chip-mt">{{ fmtSecs(slot.planned_seconds) }}</span>
                    </div>
                    <div class="pl-chip-r2">
                        <span class="pl-chip-cq">{{ slot.quote_id ? ('Q' + slot.quote_id + ' · ' + (slot.quote_name || '')) : typeLabel(slot.type) }}</span>
                        <!-- P4 job flags (⏳ ★ R tbc) so the bench sees artwork state on its own screen -->
                        <flag-pills class="sd-chip-flags" :flags="jobFlags(slot.quote_id)" compact :show-pre="false" />
                        <span v-if="slot.started_at" class="pl-pill sd-pill-started">started</span>
                    </div>
                </div>
                <span class="sd-chip-btns" @click.stop>
                    <button v-if="slot.checklist_id && !slot.started_at" type="button" class="sd-btn" :disabled="busy" @click="startSlot(slot)">▶ Start</button>
                    <button v-if="slot.checklist_id" type="button" class="sd-btn sd-btn-good" :disabled="busy" @click="tickChecklist(slot.checklist_id, true)">✓ Done</button>
                </span>
            </div>
        </draggable>
        <div v-else class="sd-empty">Nothing (left) on your list for today.</div>

        <div v-if="doneToday.length" class="sd-donewrap">
            <div v-for="slot in doneToday" :key="'d' + slot.id" class="pl-chip sd-chip pl-chip-done"
                 :style="{ '--plwc': wc.colour(slot.workcentre_id, slot.type) }"
                 @click="drawerChip = slot">
                <svg class="pl-i sd-chip-i"><use :href="'#' + wc.icon(slot.workcentre_id, slot.type)"/></svg>
                <div class="pl-chip-cx">
                    <div class="pl-chip-r1">
                        <span class="pl-chip-nm">✓ {{ slot.pretty_name }}</span>
                        <flag-pills class="sd-chip-flags" :flags="jobFlags(slot.quote_id)" compact :show-pre="false" />
                        <span class="pl-chip-mt">{{ fmtSecs(slot.planned_seconds) }}</span>
                    </div>
                </div>
                <span class="sd-chip-btns" @click.stop>
                    <button v-if="slot.checklist_id" type="button" class="sd-btn" :disabled="busy" @click="tickChecklist(slot.checklist_id, false)">↩ Undo</button>
                </span>
            </div>
        </div>
    </div>

    <!-- week peek -->
    <div class="sd-section" v-if="peekDays.length">
        <h2>Later this week <span class="sd-count">read-only — pull something forward if you're free</span></h2>
        <div class="sd-peekday" v-for="day in peekDays" :key="day.date">
            <div class="sd-peekday-hd">{{ day.label }}</div>
            <div class="pl-chip sd-chip sd-chip-peek" v-for="slot in day.slots" :key="slot.id"
                 :style="{ '--plwc': wc.colour(slot.workcentre_id, slot.type) }"
                 :title="chipTitle(slot)">
                <svg class="pl-i sd-chip-i"><use :href="'#' + wc.icon(slot.workcentre_id, slot.type)"/></svg>
                <div class="pl-chip-cx">
                    <div class="pl-chip-r1">
                        <span class="pl-chip-nm">{{ slot.pretty_name }}</span>
                        <span class="pl-chip-mt">{{ fmtSecs(slot.planned_seconds) }}</span>
                    </div>
                    <div class="pl-chip-r2">
                        <span class="pl-chip-cq">{{ slot.quote_id ? ('Q' + slot.quote_id + ' · ' + (slot.quote_name || '')) : typeLabel(slot.type) }}</span>
                        <flag-pills class="sd-chip-flags" :flags="jobFlags(slot.quote_id)" compact :show-pre="false" />
                    </div>
                </div>
                <span class="sd-chip-btns" @click.stop>
                    <button type="button" class="sd-btn" :disabled="busy" @click="moveSlotToToday(slot.id)">⤴ Pull to today</button>
                </span>
            </div>
        </div>
    </div>

    <chip-drawer v-if="drawerChip"
                 :chip="drawerChip" :board="board" :permissions="drawerPermissions"
                 @close="drawerChip = null"
                 @changed="refetch" />
</div>
</template>

<script>
import draggable from 'vuedraggable';
import ChipDrawer from './ChipDrawer';
import FlagPills from './FlagPills';
import { fmtSecs, initials, errMsg, wcLookups, copyText } from './shared';

export default {
    name: 'StaffDashboard',
    components: { draggable, ChipDrawer, FlagPills },

    data() {
        return {
            board: { dates: [], people: [], slots: [], tray: [], jobs: [], workcentres: [], carryover: [], today: '', efficiency_percent: 100, permissions: {} },
            extras: { user_id: null, user_name: '', current_work: null, stats: null, scorecard: null, notices: [] },
            todaySlots: [],       // mutable for vuedraggable
            loading: false,
            busy: false,
            error: null,
            drawerChip: null,
            calOpen: false,
            calCopied: false,
        };
    },

    created() {
        this.refetch();
    },

    computed: {
        wc() {
            return wcLookups(this.board.workcentres);
        },
        todayLabel() {
            return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
        },
        weekPercent() {
            const s = this.extras.scorecard;
            return s && s.this_week && s.this_week.personal ? s.this_week.personal.percent : null;
        },
        mySlots() {
            return this.board.slots.filter(s => s.user_id === this.extras.user_id && s.released && !s.is_draft);
        },
        doneToday() {
            return this.mySlots.filter(s => s.date === this.board.today && s.checked);
        },
        myCarryover() {
            return (this.board.carryover || []).filter(c => c.user_id === this.extras.user_id);
        },
        peekDays() {
            const byDate = {};
            this.mySlots
                .filter(s => s.date > this.board.today && !s.checked)
                .forEach(s => {
                    (byDate[s.date] = byDate[s.date] || []).push(s);
                });
            return Object.keys(byDate).sort().map(date => ({
                date,
                label: new Date(date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' }),
                slots: byDate[date].sort((a, b) => a.pos - b.pos),
            }));
        },
        drawerPermissions() {
            // Staff action set: tick/untick only — no rescheduling, no
            // est edits. The planner roles get more via /planner.
            const p = this.board.permissions || {};
            return {
                can_tick: !!this.extras.can_tick,
                can_manage: false,
                can_place: !!p.can_place,
                can_replan: !!p.can_replan,
            };
        },
    },

    methods: {
        copyFeed() {
            copyText(this.extras.feed_url)
                .then(() => {
                    this.calCopied = true;
                    setTimeout(() => { this.calCopied = false; }, 2500);
                })
                .catch(() => { this.error = 'Could not copy — select the link and copy it by hand.'; });
        },

        fmtSecs,
        initials,

        refetch() {
            this.loading = true;
            const monday = this.mondayOf(new Date());
            return Promise.all([
                window.axios.get('/planner/board', { params: { start: monday, days: 7, carryover: 'mine' } }),
                window.axios.get('/dashboard/me-extras'),
            ])
                .then(([board, extras]) => {
                    this.board = board.data;
                    this.extras = extras.data;
                    this.todaySlots = this.board.slots
                        .filter(s => s.user_id === this.extras.user_id && s.date === this.board.today
                            && s.released && !s.is_draft && !s.checked)
                        .sort((a, b) => a.pos - b.pos);
                })
                .catch(err => { this.error = errMsg(err); })
                .then(() => { this.loading = false; });
        },

        startSlot(slot) {
            this.busy = true;
            window.axios.post('/dashboard/select', { checklist_id: slot.checklist_id, slot_id: slot.id })
                .then(() => this.refetch())
                .catch(err => { this.error = errMsg(err); })
                .then(() => { this.busy = false; });
        },

        completeCurrent() {
            this.busy = true;
            window.axios.post('/dashboard/complete', { work_id: this.extras.current_work.work_id })
                .then(() => this.refetch())
                .catch(err => { this.error = errMsg(err); })
                .then(() => { this.busy = false; });
        },

        /* complete-without-start is first-class (design §4.8) */
        tickChecklist(checklistId, checked) {
            this.busy = true;
            window.axios.put('/checklist', { type: 'process', id: checklistId, checked: checked })
                .then(() => this.refetch())
                .catch(err => { this.error = errMsg(err); })
                .then(() => { this.busy = false; });
        },

        moveSlotToToday(slotId, allowOutOfSequence) {
            this.busy = true;
            window.axios.put('/planner/slot/' + slotId, {
                scheduled_date: this.board.today,
                allow_out_of_sequence: !!allowOutOfSequence,
            })
                .then(() => this.refetch())
                .catch(err => {
                    const msg = errMsg(err);
                    if (!allowOutOfSequence && /out of sequence/i.test(msg)
                        && window.confirm(msg + '\n\nAn earlier step isn\'t done yet. Pull it to today anyway? This is logged.')) {
                        this.moveSlotToToday(slotId, true);
                        return;
                    }
                    this.error = msg;
                })
                .then(() => { this.busy = false; });
        },

        onReorder(evt) {
            if (!evt.moved) return;
            const list = this.todaySlots;
            const idx = evt.moved.newIndex;
            const prev = list[idx - 1];
            const next = list[idx + 1];
            let pos = 1;
            if (prev && next) pos = (prev.pos + next.pos) / 2;
            else if (prev) pos = prev.pos + 1;
            else if (next) pos = next.pos / 2;
            window.axios.put('/planner/slot/' + evt.moved.element.id, { pos: pos })
                .then(() => this.refetch())
                .catch(err => { this.error = errMsg(err); this.refetch(); });
        },

        dismissNotice(n) {
            window.axios.post('/dashboard/dismiss-notice', { notice_id: n.id })
                .then(() => { this.extras.notices = this.extras.notices.filter(x => x.id !== n.id); })
                .catch(() => {});
        },

        typeLabel(type) {
            return type === 'delivery' ? 'Delivery' : type === 'adhoc' ? 'Ad-hoc' : '';
        },

        /* P4 job flags ride on /planner/board jobs[].flags — the same
           fetch this page already makes — so the bench reads the exact
           state the planner set. Null for ad-hoc/delivery chips. */
        jobFlags(quoteId) {
            if (!quoteId) return null;
            const job = (this.board.jobs || []).find(j => j.quote_id === quoteId);
            return job ? (job.flags || null) : null;
        },
        chipTitle(slot) {
            const f = this.jobFlags(slot.quote_id);
            return f && f.artwork === 'waiting' ? 'Waiting on artwork' : null;
        },
        mondayOf(d) {
            const c = new Date(d);
            const day = (c.getDay() + 6) % 7;
            c.setDate(c.getDate() - day);
            return c.getFullYear() + '-' + String(c.getMonth() + 1).padStart(2, '0') + '-' + String(c.getDate()).padStart(2, '0');
        },
    },
};
</script>

<style scoped>
/* Slot-driven staff dashboard — same house style as the planner
   (design §4.8: my ordered list for today). Big targets throughout. */
.sd-root{
    --pl-page:#f9f9f7; --pl-card:#fcfcfb; --pl-card2:#f4f3f0;
    --pl-ink:#0b0b0b; --pl-ink2:#52514e; --pl-ink3:#898781;
    --pl-line:#e1e0d9; --pl-good:#0ca30c; --pl-warn:#fab219; --pl-crit:#d03b3b;
    --pl-good-soft:#e3f4e3; --pl-warn-soft:#fdf3d9; --pl-crit-soft:#f9e4e4;
    background:var(--pl-page); color:var(--pl-ink);
    font:16px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;
    margin:0 -15px; padding:16px 18px 60px; min-height:80vh; max-width:920px; margin-left:auto; margin-right:auto;
}
.sd-root *{box-sizing:border-box}
.sd-root button{font:inherit; cursor:pointer}
.pl-i{width:16px; height:16px; flex:none; display:block}

.sd-notice{display:flex; justify-content:space-between; align-items:center; gap:10px;
    border-radius:10px; padding:10px 14px; margin-bottom:10px; font-weight:600; font-size:14.5px;
    background:var(--pl-card2); border:1px solid var(--pl-line)}
.sd-notice-warning{background:var(--pl-warn-soft); border-color:var(--pl-warn)}
.sd-notice-reschedule{background:#eaf1fb; border-color:#2a78d6}
.sd-notice button{border:0; background:transparent; font-weight:800; color:var(--pl-ink2)}

.pl-error{background:var(--pl-crit-soft); border:1px solid var(--pl-crit); color:#7c1d1d;
    border-radius:10px; padding:10px 14px; margin-bottom:12px; font-weight:600;
    display:flex; justify-content:space-between; gap:10px}
.pl-error button{border:0; background:transparent; font-weight:800; color:inherit}
.pl-loading{color:var(--pl-ink3); font-weight:600; padding:30px 0; text-align:center}

.sd-head{display:flex; align-items:center; gap:16px; flex-wrap:wrap; margin-bottom:14px}
.sd-title{display:flex; align-items:center; gap:12px}
.sd-title h1{font-size:22px; font-weight:800; margin:0; line-height:1.1}
.sd-date{color:var(--pl-ink2); font-weight:600; font-size:14px}
.pl-av{width:44px; height:44px; border-radius:50%; background:var(--pl-ink); color:#fff;
    display:flex; align-items:center; justify-content:center; font-weight:800; font-size:16px; flex:none}
.sd-stats{display:flex; gap:10px; margin-left:auto; flex-wrap:wrap}
.sd-stat{background:var(--pl-card); border:1px solid var(--pl-line); border-radius:12px;
    padding:8px 14px; text-align:center; min-width:86px; text-decoration:none; color:inherit}
.sd-stat b{display:block; font-size:19px; font-weight:800}
.sd-stat span{font-size:11.5px; color:var(--pl-ink3); font-weight:650}
button.sd-stat{cursor:pointer; font:inherit}

.sd-calconnect{background:var(--pl-card); border:1px solid var(--pl-line); border-radius:14px;
    box-shadow:0 1px 2px rgba(11,11,11,.06); padding:14px 16px; margin-bottom:14px}
.sd-calconnect h3{font-size:15px; font-weight:800; margin:0 0 3px}
.sd-calconnect p{font-size:13px; color:var(--pl-ink2); margin:0 0 9px}
.sd-cal-row{display:flex; gap:8px; margin-bottom:10px}
.sd-cal-row input{flex:1; min-width:0; border:1px solid var(--pl-line); border-radius:9px; padding:9px 10px;
    font:inherit; font-size:12.5px; color:var(--pl-ink2); background:var(--pl-page)}
.sd-cal-row button{border:0; background:var(--pl-ink); color:#fff; border-radius:9px; padding:9px 16px;
    font-weight:800; font-size:13.5px; cursor:pointer; font-family:inherit; flex:none}
.sd-calconnect ol{margin:0; padding-left:20px; font-size:12.5px; color:var(--pl-ink2); line-height:1.7}
.sd-stat-link:hover{background:var(--pl-card2)}

.sd-current{background:var(--pl-ink); color:#fff; border-radius:14px; padding:14px 18px; margin-bottom:16px;
    display:flex; align-items:center; gap:14px; flex-wrap:wrap}
.sd-current-badge{display:flex; align-items:center; gap:6px; font-size:11.5px; font-weight:800;
    letter-spacing:.05em; text-transform:uppercase; color:#8fe28f}
.sd-current-badge .pl-i{width:13px; height:13px}
.sd-current-nm{font-size:17px; font-weight:800}
.sd-current-meta{color:#c9c8c0; font-size:13.5px; font-weight:600}
.sd-current-done{margin-left:auto; background:var(--pl-good); color:#fff; border:0; border-radius:10px;
    font-weight:800; padding:12px 18px; font-size:15px}

.sd-section{margin-bottom:20px}
.sd-section h2{font-size:16px; font-weight:800; margin:0 0 9px; display:flex; align-items:baseline; gap:10px}
.sd-count{font-size:12.5px; color:var(--pl-ink3); font-weight:600}
.sd-list, .sd-donewrap{display:flex; flex-direction:column; gap:7px}
.sd-donewrap{margin-top:7px}
.sd-empty{color:var(--pl-ink3); font-weight:600; background:var(--pl-card); border:1px dashed var(--pl-line);
    border-radius:12px; padding:18px; text-align:center}

.pl-chip{display:flex; align-items:center; gap:10px;
    background:var(--pl-card); border:1px solid var(--pl-line); border-left:6px solid var(--plwc, var(--pl-ink3));
    border-radius:10px; padding:10px 12px; box-shadow:0 1px 2px rgba(11,11,11,.05);
    font-size:14.5px; line-height:1.35; min-height:56px; cursor:pointer; user-select:none}
.sd-chip-i{width:20px; height:20px; color:var(--plwc, var(--pl-ink3))}
.pl-chip-cx{flex:1; min-width:0}
.pl-chip-r1{display:flex; align-items:baseline; gap:8px}
.pl-chip-nm{font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0; flex:1}
.pl-chip-mt{color:var(--pl-ink2); font-size:12.5px; font-weight:700; white-space:nowrap}
.pl-chip-r2{display:flex; align-items:center; gap:7px; margin-top:1px}
.pl-chip-cq{color:var(--pl-ink3); font-size:12px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis}
.pl-chip-done{opacity:.55}
.pl-pill{font-size:10px; font-weight:800; letter-spacing:.04em; border-radius:20px; padding:1px 7px}
.sd-pill-started{background:var(--pl-good-soft); color:#0a6e0a}
.sd-chip-flags{flex:none}
.sd-grip{color:var(--pl-ink3); cursor:grab; padding:4px 2px}
.sd-grip .pl-i{width:14px; height:18px}
.sd-chip-btns{display:flex; gap:7px; flex:none}
.sd-btn{border:1px solid var(--pl-line); background:var(--pl-card2); color:var(--pl-ink);
    border-radius:9px; padding:9px 13px; font-weight:800; font-size:13.5px; white-space:nowrap}
.sd-btn-good{background:var(--pl-good); border-color:var(--pl-good); color:#fff}
.sd-btn:disabled{opacity:.55; cursor:default}

.sd-carry{display:flex; align-items:center; gap:10px; background:var(--pl-warn-soft);
    border:1px solid var(--pl-warn); border-radius:10px; padding:10px 13px; margin-bottom:7px; flex-wrap:wrap}
.sd-carry-tx{flex:1; min-width:220px; font-size:14px}
.sd-carry-tx b{display:block}
.sd-carry-tx span{color:var(--pl-ink2); font-size:12.5px; font-weight:600}

.sd-peekday{margin-bottom:10px}
.sd-peekday-hd{font-size:12.5px; font-weight:800; color:var(--pl-ink3); text-transform:uppercase;
    letter-spacing:.04em; margin-bottom:5px}
.sd-chip-peek{cursor:default; opacity:.92}
.pl-chip.sortable-ghost{opacity:.4; outline:2px dashed var(--plwc, var(--pl-ink3))}
</style>
